import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CURRICULUM } from '../data/curriculum';
import { LearningProgress, LessonStep } from '../types';
import { Check, Lock, ChevronRight, Play } from 'lucide-react';

interface StartScreenProps {
  progress: LearningProgress;
  onContinue: () => void;
  onJumpTo?: (level: number, step: LessonStep) => void;
}

type LevelState = 'completed' | 'current' | 'locked';

function getLevelState(
  levelNum: number,
  progress: LearningProgress
): LevelState {
  const history = progress.levelHistory[levelNum];
  if (history?.completedAt) return 'completed';
  if (levelNum === progress.currentLevel) return 'current';
  return 'locked';
}

/** Small circle stop for each level — mirrors KPractice TrackStop */
const LevelStop: React.FC<{
  level: (typeof CURRICULUM)[number];
  state: LevelState;
}> = ({ level, state }) => {
  return (
    <div className="flex flex-col items-center">
      {/* Current indicator dot */}
      {state === 'current' && (
        <div className="w-1.5 h-1.5 rounded-full bg-orange-400 mb-1.5" />
      )}
      {state !== 'current' && <div className="h-[12px]" />}

      {/* Level circle */}
      <div
        className={`
          w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm
          ${
            state === 'completed'
              ? 'bg-teal-400 text-white shadow-sm'
              : state === 'current'
              ? 'bg-white shadow-md ring-2 ring-orange-300 text-orange-500'
              : 'bg-gray-100 border-2 border-dashed border-gray-300 text-gray-300'
          }
        `}
      >
        {state === 'completed' ? (
          <Check className="w-5 h-5" strokeWidth={3} />
        ) : state === 'locked' ? (
          <Lock className="w-4 h-4" strokeWidth={2} />
        ) : (
          level.level
        )}
      </div>

      {/* Level name */}
      <span
        className={`
          mt-1.5 text-xs font-medium text-center leading-tight max-w-[4.5rem]
          ${
            state === 'completed'
              ? 'text-teal-600'
              : state === 'current'
              ? 'text-orange-500'
              : 'text-gray-400'
          }
        `}
      >
        {level.name}
      </span>

    </div>
  );
};

/** Arrow connector — mirrors KPractice ArrowConnector */
const Arrow: React.FC<{ completed: boolean }> = ({ completed }) => (
  <div className="flex items-center h-11 mx-1 mt-[12px]">
    <ChevronRight
      className={`w-4 h-4 ${completed ? 'text-teal-300' : 'text-gray-200'}`}
      strokeWidth={2}
    />
  </div>
);

const STEP_NAMES: Record<number, string> = {
  1: 'Introduction',
  2: 'Repeat Rule',
  3: 'Series Listening',
  4: 'Series Speaking',
  5: 'Calculator Input',
  6: 'Turnarounds',
  7: 'Mixed Review',
};

const StartScreen: React.FC<StartScreenProps> = ({ progress, onContinue, onJumpTo }) => {
  const [jumpLevel, setJumpLevel] = useState(1);
  const [jumpStep, setJumpStep] = useState<LessonStep>(1);
  const completedCount = Object.keys(progress.levelHistory).filter(
    (key) => progress.levelHistory[Number(key)]?.completedAt
  ).length;

  const isFirstTime =
    progress.currentLevel === 1 &&
    progress.currentStep === 1 &&
    completedCount === 0;

  const row1 = CURRICULUM.slice(0, 4); // Levels 1-4
  const row2 = CURRICULUM.slice(4, 8); // Levels 5-8
  const row3 = CURRICULUM.slice(8, 11); // Levels 9-11

  const renderRow = (levels: typeof CURRICULUM) =>
    levels.map((level, index) => {
      const state = getLevelState(level.level, progress);
      const isLast = index === levels.length - 1;
      const nextState =
        !isLast
          ? getLevelState(levels[index + 1].level, progress)
          : undefined;
      const arrowCompleted =
        state === 'completed' && nextState === 'completed';

      return (
        <React.Fragment key={level.level}>
          <LevelStop level={level} state={state} />
          {!isLast && <Arrow completed={arrowCompleted} />}
        </React.Fragment>
      );
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col items-center justify-center p-6">
      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-center mb-10"
      >
        <h1 className="text-5xl lg:text-6xl font-bold">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
            Learning Mode
          </span>
        </h1>
        <p className="text-lg text-gray-500 font-semibold mt-2">
          Addition Within 10
        </p>
      </motion.div>

      {/* Journey path — 3 rows of levels */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex flex-col items-center gap-4 mb-10"
      >
        {/* Progress text */}
        <p className="text-sm font-medium text-gray-400">
          {completedCount} of 11 levels completed
        </p>

        {/* Row 1: Levels 1-4 */}
        <div className="flex items-start">{renderRow(row1)}</div>

        {/* Row 2: Levels 5-8 */}
        <div className="flex items-start">{renderRow(row2)}</div>

        {/* Row 3: Levels 9-11 */}
        <div className="flex items-start">{renderRow(row3)}</div>
      </motion.div>

      {/* Start / Continue button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        className="text-center"
      >
        <motion.button
          whileHover={{ scale: 1.08, rotate: 1 }}
          whileTap={{ scale: 0.92 }}
          onClick={onContinue}
          className="h-16 lg:h-20 px-12 lg:px-16 rounded-3xl font-bold text-xl lg:text-2xl shadow-xl bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl flex items-center justify-center gap-3 mx-auto transition-all"
        >
          <Play className="w-8 h-8 lg:w-10 lg:h-10" />
          <span>{isFirstTime ? 'START' : 'CONTINUE'}</span>
        </motion.button>
      </motion.div>

      {/* Dev jump-to controls */}
      {onJumpTo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 bg-gray-800 text-white rounded-xl p-4 flex items-center gap-3 text-sm"
        >
          <span className="font-mono text-gray-400 text-xs">DEV</span>
          <select
            value={jumpLevel}
            onChange={(e) => setJumpLevel(Number(e.target.value))}
            className="bg-gray-700 text-white rounded px-2 py-1.5 text-sm"
          >
            {CURRICULUM.map((l) => (
              <option key={l.level} value={l.level}>
                L{l.level}: {l.name}
              </option>
            ))}
          </select>
          <select
            value={jumpStep}
            onChange={(e) => setJumpStep(Number(e.target.value) as LessonStep)}
            className="bg-gray-700 text-white rounded px-2 py-1.5 text-sm"
          >
            {([1, 2, 3, 4, 5, 6, 7] as LessonStep[]).map((s) => (
              <option key={s} value={s}>
                S{s}: {STEP_NAMES[s]}
              </option>
            ))}
          </select>
          <button
            onClick={() => onJumpTo(jumpLevel, jumpStep)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded font-semibold text-sm transition-colors"
          >
            Jump
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default StartScreen;
